import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { memberApi } from '@formosa/shared/api/member';
import {
  clearMemberSession,
  getMemberId,
  getMemberToken,
  getMemberUser,
  saveMemberSession,
} from '@formosa/shared/auth/tokenStorage';
import {
  AUTH_FAILURE_CLASS,
  classifyAuthBootstrapFailure,
} from '../features/offline/authFailureClassification';
import { deleteMemberOfflineSnapshots } from '../features/offline/offlineSnapshotStore';
import { normalizeOfflineMemberId } from '../features/offline/offlineConstants';

const AuthContext = createContext(null);

function resolveCachedIdentity() {
  const user = getMemberUser();
  const id = normalizeOfflineMemberId(user?.id ?? user?.memberId);
  if (!id) return null;
  return user;
}

async function wipeMemberOfflineCache(memberId) {
  const mid = normalizeOfflineMemberId(memberId);
  if (!mid) return;
  try {
    await deleteMemberOfflineSnapshots(mid);
  } catch {
    /* Privacy wipe failure must not trap logout / auth transition. */
  }
}

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('unknown');
  const [user, setUser] = useState(null);

  const becomeAnonymous = useCallback(() => {
    clearMemberSession();
    setUser(null);
    setStatus('anonymous');
  }, []);

  useEffect(() => {
    let active = true;
    const bootstrap = async () => {
      const token = getMemberToken();
      if (!token) {
        if (active) setStatus('anonymous');
        return;
      }
      try {
        const member = await memberApi.getInfo();
        if (!active) return;
        saveMemberSession({ authToken: token, member });
        setUser(member || getMemberUser());
        setStatus('authenticated');
      } catch (cause) {
        if (!active) return;
        const kind = classifyAuthBootstrapFailure(cause);
        if (kind === AUTH_FAILURE_CLASS.TRANSPORT_FAILURE) {
          const cached = resolveCachedIdentity();
          if (cached) {
            setUser(cached);
            setStatus('offline_cached');
            return;
          }
          // Token present but no stable cached identity: do not invent offline_cached.
          // Keep token for a later online retry; do not unlock private shell.
          setUser(null);
          setStatus('anonymous');
          return;
        }
        const memberId = getMemberId() ?? normalizeOfflineMemberId(getMemberUser()?.id);
        await wipeMemberOfflineCache(memberId);
        if (!active) return;
        becomeAnonymous();
      }
    };
    bootstrap();
    return () => {
      active = false;
    };
  }, [becomeAnonymous]);

  useEffect(() => {
    const onAuthExpired = () => {
      const memberId = getMemberId() ?? normalizeOfflineMemberId(getMemberUser()?.id);
      void wipeMemberOfflineCache(memberId).finally(() => {
        becomeAnonymous();
      });
    };
    window.addEventListener('formosa:auth-expired', onAuthExpired);
    return () => window.removeEventListener('formosa:auth-expired', onAuthExpired);
  }, [becomeAnonymous]);

  const completeAuth = useCallback((data) => {
    saveMemberSession(data || {});
    setUser(data?.member || null);
    setStatus('authenticated');
    return data;
  }, []);

  const login = useCallback(async (credentials) => {
    const data = await memberApi.login(credentials);
    return completeAuth(data);
  }, [completeAuth]);

  const logout = useCallback(async () => {
    const memberId = getMemberId() ?? normalizeOfflineMemberId(getMemberUser()?.id);
    await wipeMemberOfflineCache(memberId);
    becomeAnonymous();
  }, [becomeAnonymous]);

  const refreshUser = useCallback(async () => {
    const token = getMemberToken();
    if (!token) {
      becomeAnonymous();
      return null;
    }
    try {
      const member = await memberApi.getInfo();
      saveMemberSession({ authToken: token, member });
      setUser(member || getMemberUser());
      setStatus('authenticated');
      return member;
    } catch (cause) {
      const kind = classifyAuthBootstrapFailure(cause);
      if (kind === AUTH_FAILURE_CLASS.TRANSPORT_FAILURE) {
        const cached = resolveCachedIdentity();
        if (cached) {
          setUser(cached);
          setStatus('offline_cached');
          return cached;
        }
        setUser(null);
        setStatus('anonymous');
        return null;
      }
      const memberId = getMemberId() ?? normalizeOfflineMemberId(getMemberUser()?.id);
      await wipeMemberOfflineCache(memberId);
      becomeAnonymous();
      return null;
    }
  }, [becomeAnonymous]);

  const value = useMemo(() => ({
    status,
    user,
    isAuthenticated: status === 'authenticated',
    isOfflineCached: status === 'offline_cached',
    login,
    completeAuth,
    logout,
    refreshUser,
  }), [status, user, login, completeAuth, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
